import classNames from 'classnames';
import { useTheme } from 'next-themes';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContext } from 'react';
import { ChevronsRight, Moon } from '@styled-icons/feather';
import { Eye } from '@styled-icons/octicons';
import { Redo, Undo, OpenInNew } from '@styled-icons/material';
import { Text } from '@typedream/ui';
import mixpanel from 'mixpanel-browser';
import {
  RENDERER_DOMAIN,
  SUBSCRIPTION_PLAN_BADGE_VARIANT,
  SUBSCRIPTION_PLAN_NAME,
} from '@/constants';
import { EVENT } from '@/analytics';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import { useCurrentSubscriptionPlan } from '@/utils/useCurrentSubscriptionPlan';
import { SUBSCRIPTION_PLAN } from '@/types';
import { useFeatureFlag, useGetSpaceQuery } from '@/store';
import flags from '@/store/flags';
import {
  DEFAULT_SIDEBAR_WIDTH,
  DARK_THEME,
  LIGHT_THEME,
  DEFAULT_NAVBAR_HEIGHT,
  EXTERNAL_UNDO_BUTTON_ID,
  EXTERNAL_REDO_BUTTON_ID,
} from '../../sharedComponents/TextEditor/constants';
import {
  SidebarContext,
  SidebarDispatchContext,
} from '../../sharedComponents/TextEditor/context/SidebarContextProvider';
import styles from '../../styles/Home.module.css';
import XRayModeToggle from '../XRayModeToggle';
import Tooltip from '../../sharedComponents/TextEditor/components/Tooltip';
import PublishButton from '../PublishButton';
import useCmdCtrl from '../../sharedComponents/TextEditor/utils/useCmdCtrl';
import { TextEditorContext } from '../../sharedComponents/TextEditor/context/TextEditorContext';
import EmptyPageButton from '../EmptyPageButton';
import useThemeFromSpace from '../../utils/useThemeFromSpace';
import Badge from '../Badge';
import { styDomain } from './style';
import UpgradeButton from '../UpgradeButton';

const SPACE_ID_THEME_STORAGE_KEY = 'spaceThemes';
function EditingNavbar({
  previewLink,
  isShowEmptyPageButton,
}: {
  previewLink?: string;
  isShowEmptyPageButton?: boolean;
}) {
  const { theme, setTheme } = useTheme();

  const router = useRouter();
  const { slug } = router.query;
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { data: space } = useGetSpaceQuery([spaceID]);
  const sidebarOpen = useContext(SidebarContext);
  const setsidebarOpen = useContext(SidebarDispatchContext);
  const sidebarWidth = sidebarOpen ? DEFAULT_SIDEBAR_WIDTH : 0;
  const symbol = useCmdCtrl();
  const { save } = useContext(TextEditorContext);
  const { plan } = useCurrentSubscriptionPlan(spaceID);

  const isDreamEditorEnabled = useFeatureFlag(flags.DreamEditor);

  useThemeFromSpace(spaceID);

  return (
    <>
      <div style={{ height: DEFAULT_NAVBAR_HEIGHT, flexShrink: 0 }} />
      <div
        className={styles.navbar}
        style={{
          zIndex: isDreamEditorEnabled ? 0 : 1,
          left: sidebarWidth,
          height: DEFAULT_NAVBAR_HEIGHT,
          width: `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        <div className={classNames('column', styles.navbarColumn)}>
          {!sidebarOpen && setsidebarOpen && typeof setsidebarOpen === 'function' && (
            <div
              className={classNames(styles.chevronSidebar, 'with-light-text-color', 'mr-2')}
              onClick={() => setsidebarOpen(true)}
              role="none"
            >
              <ChevronsRight size={20} />
            </div>
          )}

          <Tooltip
            center={
              space?.custom_domain || space?.slug ? `${space.slug}.${RENDERER_DOMAIN}` : undefined
            }
            right={
              <span className="is-flex is-align-items-center">
                Click to Open
                <OpenInNew size={14} className="ml-1" />
              </span>
            }
          >
            <a
              css={styDomain}
              href={`https://${space?.custom_domain || `${space?.slug}.${RENDERER_DOMAIN}`}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Text size="sm" variant="mute">
                {space?.custom_domain || space?.slug
                  ? `${space.slug}.${RENDERER_DOMAIN}`
                  : undefined}
              </Text>
            </a>
          </Tooltip>
          {plan !== SUBSCRIPTION_PLAN.PRO && (
            <Link href={`/settings/${space?.slug}/${spaceID}/billing`} className="ml-2">
              <Badge variant={SUBSCRIPTION_PLAN_BADGE_VARIANT[plan]}>
                <p className="has-text-weight-bold is-uppercase">{SUBSCRIPTION_PLAN_NAME[plan]}</p>
              </Badge>
            </Link>
          )}
          {isShowEmptyPageButton && <EmptyPageButton />}
        </div>
        <div className={classNames('column is-narrow', styles.navbarColumn)}>
          <div
            className={classNames(
              styles.navbarItem,
              'is-flex is-align-items-center is-justify-content-center'
            )}
          >
            <Tooltip center="Undo" right={`${symbol} Z`}>
              <div className={styles.navbarItemButton} id={EXTERNAL_UNDO_BUTTON_ID}>
                <Undo
                  size={18}
                  style={{
                    color: theme === DARK_THEME ? 'white' : 'var(--text-lighter)',
                    cursor: 'pointer',
                  }}
                />
              </div>
            </Tooltip>
            <Tooltip center="Redo" right={`${symbol} Shift Z`}>
              <div className={styles.navbarItemButton} id={EXTERNAL_REDO_BUTTON_ID}>
                <Redo
                  size={18}
                  style={{
                    color: theme === DARK_THEME ? 'white' : 'var(--text-lighter)',
                    cursor: 'pointer',
                  }}
                />
              </div>
            </Tooltip>
          </div>
          <div className={styles.navbarItem}>
            <XRayModeToggle />
          </div>
          <div className={styles.navbarItem}>
            <Tooltip center="Preview">
              <a
                target="_new"
                className="with-light-text-color is-size-7"
                href={previewLink || `/pr/${slug}/${spaceID}/${pageID}`}
              >
                <div
                  role="none"
                  className={styles.navbarItemButton}
                  onClick={() => {
                    save(false);
                    mixpanel.track(EVENT.PREVIEW);
                  }}
                >
                  <Eye size={18} style={{ marginRight: 5 }} />
                  Preview
                </div>
              </a>
            </Tooltip>
          </div>
          <div className={styles.navbarItem}>
            <div className={classNames('field', styles.darkModeToggleContainer)}>
              <input
                id="darkMode"
                type="checkbox"
                // name="darkMode"
                className="switch is-rounded is-dark"
                checked={theme === DARK_THEME}
                onChange={(e) => setTheme(theme === DARK_THEME ? LIGHT_THEME : DARK_THEME)}
              />
              <label htmlFor="darkMode" className={styles.darkModeToggle}>
                <Moon
                  size={18}
                  style={{
                    color: theme === DARK_THEME ? 'white' : 'var(--text-light)',
                  }}
                />
              </label>
            </div>
          </div>

          {plan === SUBSCRIPTION_PLAN.FREE && (
            <div style={{ padding: '0px 5px', height: '100%' }}>
              <UpgradeButton
                className="has-fullheight"
                slug={space?.slug}
                size="xxs"
                spaceID={spaceID}
              />
            </div>
          )}
          <div className={styles.navbarItem}>
            <PublishButton />
          </div>
        </div>
      </div>
    </>
  );
}

EditingNavbar.defaultProps = {
  previewLink: undefined,
  isShowEmptyPageButton: false,
};

export default EditingNavbar;
